import React, { Component } from 'react';
import './shared.css';
import T from 'i18n-react';
import FontAwesome from 'react-fontawesome';

class ValidatingTextInput extends Component
{
    constructor(props) {
        super(props)

        this.state = {
            labelText: this.props.labelText,
            placeholderText: this.props.placeholderText,
            validationFailedText: this.props.validationFailedText,
            validator: this.props.validator,
            hasValidated: false,
            hasErrors: false,
            errorType: null,
            value: this.props.value || "",
            mainDivAdditionalClass: this.props.mainDivAdditionalClass,
            name: this.props.name,
            isRequired: Boolean(this.props.isRequired),
            validationRequired: Boolean(this.props.validationRequired),  // indicates we must validate for empty on blur
            missingRequiredField: this.props.missingRequiredField,
            isNumber: this.props.isNumber,
            maxLength: this.props.maxLength || 1024,
            renderErrorStyle: this.props.renderErrorStyle || false,
            renderErrorBorderOnly: this.props.renderErrorBorderOnly || false,
            autoComplete: this.props.autoComplete,
            validateOnChange: Boolean(this.props.validateOnChange),
        }
    }

    componentDidMount() {
        if (this.state.value && this.state.validator) {
            const validation = this.validateText(this.state.value);
            if (!validation.status) {
                this.setState({ 
                    hasValidated: true, 
                    hasErrors: true, 
                    errorType: validation.errorType 
                });
            }
        }
    }

    componentWillUnmount() {
        this._isUpdating = false;
        this._blurScheduled = false;
    }

    componentDidUpdate(prevProps, prevState) {
        if (this._isUpdating) {
            return;
        }

        const updates = {};

        if (this.props.validator !== prevProps.validator && this.props.validator !== this.state.validator) {
            updates.validator = this.props.validator;
        }

        if (this.props.value !== prevProps.value && this.props.value !== this.state.value) {
            const newValue = this.props.value;
            updates.value = newValue;
            
            if (newValue && (this.state.validator || this.props.validator)) {
                const validator = this.props.validator || this.state.validator;
                const validation = this.validateTextWithValidator(newValue, validator);
                if (!validation.status) {
                    updates.hasValidated = true;
                    updates.hasErrors = true;
                    updates.errorType = validation.errorType;
                } else if (this.state.hasValidated) {
                    updates.hasErrors = false;
                    updates.errorType = null;
                }
            }
        }
        
        if (this.props.labelText !== prevProps.labelText && this.props.labelText !== this.state.labelText) {
            updates.labelText = this.props.labelText;
        }

        if (this.props.validationFailedText !== prevProps.validationFailedText && 
            this.props.validationFailedText !== this.state.validationFailedText) {
            updates.validationFailedText = this.props.validationFailedText;
        }

        if (this.props.missingRequiredField !== prevProps.missingRequiredField && 
            this.props.missingRequiredField !== this.state.missingRequiredField) {
            updates.missingRequiredField = this.props.missingRequiredField;
            if (this.props.missingRequiredField && !this.state.value &&
                (this.state.isRequired || this.state.validationRequired)) {
                updates.hasErrors = true;
                updates.hasValidated = true;
                updates.errorType = 'required';
            }
        }

        if (this.props.isNumber !== prevProps.isNumber && this.props.isNumber !== this.state.isNumber) {
            updates.isNumber = this.props.isNumber;
        }

        if (this.props.maxLength && this.props.maxLength !== prevProps.maxLength && 
            this.props.maxLength !== this.state.maxLength) {
            updates.maxLength = this.props.maxLength;
        }

        if (this.props.validationRequired !== prevProps.validationRequired && 
            this.props.validationRequired !== this.state.validationRequired) {
            updates.validationRequired = this.props.validationRequired;
        }

        if (this.props.renderErrorStyle !== prevProps.renderErrorStyle && 
            this.props.renderErrorStyle !== this.state.renderErrorStyle) {
            updates.renderErrorStyle = this.props.renderErrorStyle;
        }

        if (this.props.renderErrorBorderOnly !== prevProps.renderErrorBorderOnly && 
            Boolean(this.props.renderErrorBorderOnly) !== this.state.renderErrorBorderOnly) {
            updates.renderErrorBorderOnly = Boolean(this.props.renderErrorBorderOnly);
        }

        if (this.props.learnMoreId !== prevProps.learnMoreId && 
            this.props.learnMoreId !== this.state.learnMoreId) {
            updates.learnMoreId = this.props.learnMoreId;
        }

        if (this.props.autoComplete !== prevProps.autoComplete && 
            this.props.autoComplete !== this.state.autoComplete) {
            updates.autoComplete = this.props.autoComplete;
        }

        if (this.props.validateOnChange !== prevProps.validateOnChange &&
            Boolean(this.props.validateOnChange) !== this.state.validateOnChange) {
            updates.validateOnChange = Boolean(this.props.validateOnChange);
        }

        if (Object.keys(updates).length > 0) {
            this._isUpdating = true;
            this.setState(updates, () => {
                this._isUpdating = false;
            });
        }
    }

    validateTextWithValidator(text, validator) {
        if ((this.state.isRequired || this.state.validationRequired) && !Boolean(text)) {
            return { status: false, errorType: 'required' }
        }
        if (validator && !validator(text)) {
            return { status: false, errorType: 'validatory' }
        }
        return { status: true }
    }

    validateText(currText) {
        const validator = this.props.validator || this.state.validator;
        if ((this.state.isRequired || this.state.validationRequired) && !Boolean(currText)) {
            return { status: false, errorType: 'required' }
        }
        if (validator && !validator(currText)) {
            return { status: false, errorType: 'validatory' }
        }
        return { status: true }
    }

    handleTextChange(ev) {
        const value = ev.target.value;
        
        const newState = {
            ...this.state,
            value: value 
        };
        
        if (this.state.hasValidated || this.state.validateOnChange) {
            const validation = this.validateText(value);
            newState.hasValidated = true;
            newState.hasErrors = !validation.status;
            newState.errorType = validation.errorType;
        }

        this.setState(newState, () => {
            if (this.props.onChange) {
                this.props.onChange(ev, value);
            }
        });
    }

    handleBlur() {
        const validation = this.validateText(this.state.value);
        
        this.setState({ 
            hasValidated: true, 
            hasErrors: !validation.status, 
            errorType: validation.errorType 
        });

        if (this.state.value && this.state.value.trim() !== this.state.value) {
            const trimmedValue = this.state.value.trim();
            this.setState({ value: trimmedValue });
            
            if (this.props.onChange) {
                const event = { target: { value: trimmedValue, name: this.state.name } };
                this.props.onChange(event, trimmedValue);
            }
        }

        if (this.props.onBlur) {
            this.props.onBlur()
        }
    }

    onKeyDown(ev) {
        if (this.props.onKeyDown) {
            this.props.onKeyDown(ev);
        }
    }

    render() {
        var mainDivClassName = "validatingTextInputStyle"
        const labelText = this.props.labelText !== undefined ? this.props.labelText : this.state.labelText
        const validationFailedText = this.props.validationFailedText !== undefined ? this.props.validationFailedText : this.state.validationFailedText

        var topTextStyle = null
        if (this.props.extraTopSpacing) {
            topTextStyle = {
                height: '36px'
            }
        }
        
        const showMissingRequiredError = this.props.missingRequiredField && 
                                         (this.state.isRequired || this.state.validationRequired) && 
                                         !this.state.value;
      
        if (this.state.hasErrors || this.state.renderErrorStyle || this.state.renderErrorBorderOnly || showMissingRequiredError) {
            var isDepError = Boolean(this.props.missingFieldText);
            var errorColor = isDepError ? '#BA1A1A' : '#E70000';
            var errorClassName = isDepError ? "validationErrorStyle dep-validation-error" : "validationErrorStyle";
            var valErrors = this.state.renderErrorBorderOnly ? (
                <div className="validationErrorStyle">
                    &nbsp;
                    </div>
                ) : (
                <div className={errorClassName}>
                    <FontAwesome className='fa-exclamation-circle' name='fa-exclamation-circle' size='lg' style={{ textShadow: '0 1px 0 rgba(0, 0, 0, 0)', color: errorColor, align: 'center' }} />
                    <span style={{ marginLeft: 10 }}>{(this.state.errorType === 'required' || showMissingRequiredError) ? (this.props.missingFieldText || T.translate('Common.Errors.MissingRequiredField')) : validationFailedText}</span>
                </div>
            )
            return (
                <div className="defaultErrorStyles validatingTextInputStyle">
                    <div className="red formLabelStyle" style={topTextStyle}>
                        {labelText}{(this.state.isRequired && !this.props.hideAsterisk) ? "*" : ""}
                    </div>
                    {this.state.autoComplete &&
                        <input className="rte-text-field" type={this.state.isNumber ? "tel" : "text"} name={this.state.name}
                            value={this.state.value} onBlur={(ev) => { this.handleBlur() }}
                            onChange={(ev) => { this.handleTextChange(ev) }} maxLength={this.state.maxLength}
                            onKeyDown={(ev) => { this.onKeyDown(ev) }} heap-ignore="true"
                            autoComplete={this.state.autoComplete} placeholder={this.props.placeholderText} />
                    }
                    {!this.state.autoComplete && 
                        <input className="rte-text-field" type={this.state.isNumber ? "tel" : "text"} name={this.state.name}
                            value={this.state.value} onBlur={(ev) => { this.handleBlur() }}
                            heap-ignore="true"
                            onChange={(ev) => { this.handleTextChange(ev) }} maxLength={this.state.maxLength}
                            onKeyDown={(ev) => { this.onKeyDown(ev) }} placeholder={this.props.placeholderText} />
                    }

                    {valErrors}
                </div>
            )
        } else {
            return (
                <div className={mainDivClassName}>
                    <div className="formLabelStyle" style={topTextStyle}>
                        {labelText}{this.state.isRequired && !this.props.hideAsterisk ? "*" : ""}
                    </div>
                    {this.state.autoComplete &&
                        <input className="rte-text-field" type={this.state.isNumber ? "tel" : "text"} name={this.state.name}
                            value={this.state.value} onBlur={(ev) => { this.handleBlur() }}
                            heap-ignore="true" 
                            onChange={(ev) => { this.handleTextChange(ev) }} maxLength={this.state.maxLength}
                            onKeyDown={(ev) => { this.onKeyDown(ev) }}
                            autoComplete={this.state.autoComplete} placeholder={this.props.placeholderText}
                        />
                    }
                    {!this.state.autoComplete &&
                        <input className="rte-text-field" type={this.state.isNumber ? "tel" : "text"} name={this.state.name}
                            heap-ignore="true"
                            value={this.state.value} onBlur={(ev) => { this.handleBlur() }}
                            onChange={(ev) => { this.handleTextChange(ev) }} maxLength={this.state.maxLength}
                            onKeyDown={(ev) => { this.onKeyDown(ev) }} placeholder={this.props.placeholderText}
                        />
                    }
                    <div className="validationErrorStyle">
                        &nbsp;
                    </div>
                </div>
            )
            
        }
    }
}


export default ValidatingTextInput;